import React, { useEffect, useMemo, useState } from 'react';
import { ResponsiveBar } from '@nivo/bar';
import NivoTextParser from './NivoTextParser';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClose, faExpand } from '@fortawesome/free-solid-svg-icons';
import Prediction from '../../../../components/modal/NavPanes/Prediction';
import { CloseButton } from 'reactstrap';
import { CloseIcon } from '../../../Common/SvgIcons/WorkflowIcons';
import nivoTheme from './nivoTheme';

const fallbackBarColors = ["#2673e8", "#25a0c7", "#6f7ee8", "#35a55f", "#e19b2d"];
const metadataBarKeys = new Set(["id", "name", "class", "label", "category"]);

const isPlainObject = (value) =>
    value !== null && typeof value === "object" && !Array.isArray(value);

const isNumericLike = (value) => {
    if (value === null || value === undefined || value === "") return false;
    return Number.isFinite(Number(value));
};

const isBarValueKey = (key, rows) => {
    const normalizedKey = key.toLowerCase();

    if (metadataBarKeys.has(normalizedKey) || normalizedKey.includes("color")) {
        return false;
    }

    return rows.some((row) => isNumericLike(row?.[key]));
};

const getBarValueKeys = (rows) => {
    if (!Array.isArray(rows) || !rows.length) return [];

    const candidateKeys = Object.keys(rows[0] || {}).filter((key) => {
        const normalizedKey = key.toLowerCase();
        return !metadataBarKeys.has(normalizedKey) && !normalizedKey.includes("color");
    });
    const numericKeys = candidateKeys.filter((key) => isBarValueKey(key, rows));

    return numericKeys.length ? numericKeys : candidateKeys;
};

const normalizeBarValues = (rows, keys) =>
    rows.map((row) => {
        const nextRow = { ...row };

        keys.forEach((key) => {
            if (isNumericLike(nextRow[key])) {
                nextRow[key] = Number(nextRow[key]);
            }
        });

        return nextRow;
    });

const getGroupedBarPlots = (plotOptions) => {
    if (!isPlainObject(plotOptions)) return [];

    return Object.entries(plotOptions)
        .filter(([, plot]) => isPlainObject(plot) && plot.type?.match("bar") && Array.isArray(plot.data))
        .map(([key, plot]) => ({
            key,
            label: key,
            data: plot.data,
            xlabel: plot.xlabel,
            ylabel: plot.ylabel,
        }));
};

export default function BarNivo({ data, title, height, class_data, xlabel, ylabel, evaluation_id, modelName, task_type, cssManage, plotOptions, integerYAxisTicks = false, groupMode = "stacked", showLegend = false }) {
    const classMapping = Object.entries(class_data || {});
    const groupedBarPlots = useMemo(() => getGroupedBarPlots(plotOptions), [plotOptions]);
    const [selectedCategory, setSelectedCategory] = useState(classMapping[0]?.[1]);
    const [selectedPlotKey, setSelectedPlotKey] = useState(groupedBarPlots[0]?.key);
    const [selectedDataset, setSelectedDataset] = useState("Train");
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isHovered, setIsHovered] = useState(false);
    const [sliderIndex, setSliderIndex] = useState(0);
    const [predictions, setPredictions] = useState(false);
    const [selectedBarData, setSelectedBarData] = useState(null);

    // state for images popup
    const [images, setImages] = useState([]);
    const [isImageModalOpen, setIsImageModalOpen] = useState(false);

    useEffect(() => {
        if (!groupedBarPlots.length) return;

        setSelectedPlotKey((currentKey) =>
            groupedBarPlots.some((plot) => plot.key === currentKey)
                ? currentKey
                : groupedBarPlots[0].key
        );
    }, [groupedBarPlots]);

    const selectedPlot = groupedBarPlots.find((plot) => plot.key === selectedPlotKey) || groupedBarPlots[0];
    const chartData = selectedPlot?.data ?? data;
    const activeXlabel = selectedPlot?.xlabel ?? xlabel;
    const activeYlabel = selectedPlot?.ylabel ?? ylabel;

    const getFormattedData = (data, title, category, datasetType) => {
        if (title === "Classwise keypoints distribution") {
            const selectedIndex = classMapping.find(([_, value]) => value === category)?.[0];
            const selectedData = data[selectedIndex];
            const selectedSeries = Object.values(selectedData || {})[0];

            if (!Array.isArray(selectedSeries)) return [];

            return selectedSeries.map((item) => ({
                id: item.id,
                train_images_count: item.train_images_count,
                test_images_count: item.test_images_count,
                val_images_count: item.val_images_count,
                train_color: item.train_color,
                test_color: item.test_color,
                val_color: item.val_color
            }));
        }
        return data;
    };

    const formattedData = getFormattedData(chartData, title, selectedCategory, selectedDataset);
    const transformedData = Array.isArray(formattedData) ? formattedData : [];

    const keys = title === "Classwise keypoints distribution"
        ? ["train_images_count", "test_images_count", "val_images_count"]
        : getBarValueKeys(transformedData);
    const barData = normalizeBarValues(transformedData, keys);
    const chartBodyHeight = "calc(100% - 34px)";
    const modalChartBodyHeight = "calc(100% - 50px)";
    const usesIntegerValueTicks =
        integerYAxisTicks ||
        /objects per image distribution/i.test(String(title || "")) ||
        /number of images/i.test(String(activeYlabel || "")) ||
        /\bobjects?\b/i.test(String(activeYlabel || ""));
    const integerValueTicks = useMemo(() => {
        if (!usesIntegerValueTicks) return undefined;

        const maximumValue = Math.max(
            0,
            ...barData.map((row) =>
                keys.reduce((sum, key) => sum + (Number(row?.[key]) || 0), 0)
            )
        );
        const step = Math.max(1, Math.ceil(maximumValue / 10));
        const axisMaximum = Math.max(
            step,
            Math.ceil(maximumValue / step) * step
        );

        return Array.from(
            { length: Math.floor(axisMaximum / step) + 1 },
            (_, index) => index * step
        );
    }, [barData, keys, usesIntegerValueTicks]);

    const getVisibleTickValues = () => {
        const maxVisibleTicks = isModalOpen ? 20 : 10;

        if (barData.length <= maxVisibleTicks) {
            return barData.map((item) => item.id);
        }

        const lastIndex = barData.length - 1;
        const tickIndexes = Array.from(
            { length: maxVisibleTicks },
            (_, index) => Math.round((index * lastIndex) / (maxVisibleTicks - 1))
        );

        return [...new Set(tickIndexes)].map((index) => barData[index].id);
    };

    const getFallbackColor = (datum) => {
        const dataIndex = barData.findIndex((item) => item.id === datum?.id || item.name === datum?.name);
        return fallbackBarColors[(dataIndex >= 0 ? dataIndex : 0) % fallbackBarColors.length];
    };

    const getLegendColor = (key) => {
        const specialColor = key === "train_images_count"
            ? barData.find((item) => item?.train_color)?.train_color
            : key === "test_images_count"
                ? barData.find((item) => item?.test_color)?.test_color
                : key === "val_images_count"
                    ? barData.find((item) => item?.val_color)?.val_color
                    : undefined;
        if (specialColor) return String(specialColor);

        const explicitColor = barData.find((item) => item?.[`${key} Color`])?.[`${key} Color`];
        if (explicitColor) return String(explicitColor);

        const keyIndex = keys.indexOf(key);
        return fallbackBarColors[(keyIndex >= 0 ? keyIndex : 0) % fallbackBarColors.length];
    };

    const renderLegend = () => showLegend && keys.length > 0 && (
        <aside
            className="evaluation-analytics-side-legend"
            aria-label={`${title || "Bar chart"} legend`}
            onClick={(event) => event.stopPropagation()}
        >
            <div className="evaluation-analytics-side-legend__items">
                {keys.map((key) => (
                    <span className="evaluation-analytics-side-legend__item" key={key} title={String(key)}>
                        <i style={{ backgroundColor: getLegendColor(key) }} />
                        <span>{String(key).replaceAll("_", " ")}</span>
                    </span>
                ))}
            </div>
        </aside>
    );

    const renderPlotSelector = (context = "card") => groupedBarPlots.length > 0 && (
        <div
            className={`grouped-plot-select-wrap grouped-plot-select-wrap--${context}`}
            onClick={(e) => e.stopPropagation()}
        >
            <label className="grouped-plot-select-label mb-0">
                <span className="visually-hidden">Select chart for {title}</span>
                <select
                    className="prediction-search-select grouped-plot-select"
                    value={selectedPlot?.key || ""}
                    onChange={(event) => setSelectedPlotKey(event.target.value)}
                    aria-label={`Select chart for ${title}`}
                >
                    {groupedBarPlots.map((plot) => (
                        <option key={plot.key} value={plot.key}>
                            {plot.label}
                        </option>
                    ))}
                </select>
            </label>
        </div>
    );


    const renderChart = (chartWidth) => (
        <ResponsiveBar
            data={barData}
            keys={keys}
            margin={{ top: 20, right: 20, bottom: 125, left: 80 }}
            innerPadding={groupMode === "stacked" ? 0 : 4}
            groupMode={groupMode}
            valueScale={{ type: "linear" }}
            indexScale={{ type: 'band', round: true }}
            indexBy="id"
            tooltip={({ id, value, indexValue }) => (
                <div className='shadow'
                    style={{
                        padding: '6px 10px',
                        background: 'var(--bg-color8)',
                        borderRadius: 4,
                        color: 'var(--text-color)',
                        fontSize: 12,
                        whiteSpace: 'nowrap',
                        // transform: 'translateX(-50%)'
                    }}
                >
                    <strong>{indexValue}</strong>
                    <br />
                    {id}: {value}
                </div>
            )}
            


            colors={({ id, data }) => {
                if (id === "train_images_count") return data.train_color;
                if (id === "test_images_count") return data.test_color;
                if (id === "val_images_count") return data.val_color;
                if (data?.[`${id} Color`]) return String(data[`${id} Color`]);
                return showLegend ? getLegendColor(id) : getFallbackColor(data);
            }}
            colorBy='indexValue'
            axisTop={null}
            axisRight={null}
            axisLeft={{
                orient: 'left',
                tickSize: 5,
                tickPadding: 5,
                tickRotation: 0,
                tickValues: integerValueTicks,
                legend: activeYlabel,
                legendPosition: 'middle',
                legendOffset: -66
            }}
            axisBottom={{
                orient: 'bottom',
                tickSize: 5,
                tickPadding: 5,
                tickRotation: transformedData.length > 2 ? -45 : 0,
                legend: activeXlabel,
                legendOffset: 104,
                legendPosition: 'middle',
                tickValues: getVisibleTickValues(),
            }}
            enableGridY={false}
            enableLabel={false}
            theme={nivoTheme}
            role="application"
            ariaLabel="Nivo bar chart"
            onClick={(barData) => {
                if (!isModalOpen) return;
                if (!evaluation_id) return;
                // barData contains: { id, value, indexValue, data, color, ... }
                // setIsModalOpen(false);
                setSelectedBarData({
                    ...barData,
                    chartTitle: title,   // 👈 also attach the chart title
                });
                setPredictions(true);
            }}
        />
    );

    return (
        <div className={`chart ${title === "Classwise keypoints distribution" ? "pb-5" : ""} ${!cssManage && 'badge-border p-4'} `} style={{ height: height ?? "400px", position: 'relative' }}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            onClick={() => {!isModalOpen && setIsModalOpen(true)}}
        >
            <NivoTextParser title={title} />

            {isHovered && (
                <div
                    style={{ position: 'absolute', top: 10, right: 10, cursor: 'pointer' }}
                    onClick={() => setIsModalOpen(true)}
                >
                    <FontAwesomeIcon icon={faExpand} title='Expand' />
                </div>
            )}

            {renderPlotSelector("card")}

            {title === "Classwise keypoints distribution" && (
                <div className='d-flex' style={{ position: 'relative', top: "-2%", left: "85%" }}>
                    <div style={{ display: 'flex', flexDirection: "column", zIndex: "999", alignItems: "flex-start" }}>
                        {classMapping.map(([key, filter]) => (
                            <label className='text' key={key} style={{ marginBottom: '5px' }}>
                                <input
                                    type="radio"
                                    value={filter}
                                    checked={selectedCategory === filter}
                                    onChange={() => setSelectedCategory(filter)}
                                />
                                &nbsp; {filter}
                            </label>
                        ))}
                    </div>
                </div>
            )}

            <div style={{ height: chartBodyHeight, minHeight: 0 }}>
                {showLegend ? <div className="evaluation-analytics-bar-layout has-legend">
                    <div className="evaluation-analytics-bar-canvas">
                        {renderChart('100%')}
                    </div>
                    {renderLegend()}
                </div> : renderChart('100%')}
            </div>

            {predictions && (
                <div
                    className={`sample-bg mt-0 mt-md-4`}
                    style={{
                        position: "fixed",
                        top: "1",
                    }}
                    onClick={() => setPredictions(false)}
                >
                    <div
                        className='modal-905 w-100 mx-0 mb-0'
                        onClick={(e) => e.stopPropagation()}
                    >
                        <Prediction
                            type="bars"

                            sliderIndex={sliderIndex}
                            setSliderIndex={setSliderIndex}
                            setPredictions={setPredictions}
                            barData={selectedBarData}   // 👈 clicked bar + chart title
                            bar_url={`evaluations`}
                            id={evaluation_id}
                            modelName={modelName}
                            tasktype={task_type}
                        />


                    </div>
                </div>
            )}

            {isModalOpen && (
                <div
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100vh',
                        backgroundColor: 'rgba(0, 0, 0, 0.7)',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        zIndex: 6
                    }}
                    onClick={() => setIsModalOpen(false)}
                >
                    <div className='pt-0 pe-0 container-card w-75 w-md-100'
                        style={{
                            backgroundColor: 'var(--bg-color8)',
                            padding: '50px',
                            height: '70%',
                            position: 'relative',
                           overflow: 'visible'
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <button className='plain-button text-danger' onClick={() => setIsModalOpen(false)}
                            style={{ position: 'absolute', top: 10, right: 10, cursor: 'pointer' }}
                        >
                            <CloseIcon className='wf-icon text-secondary' />
                        </button>
                        <div className='p-3' style={{ width: '100%', height: '100%' }}>
                            <div style={{ height: "50px" }}>
                                <NivoTextParser title={title} />
                            </div>
                            {renderPlotSelector("modal")}
                            <div style={{ height: modalChartBodyHeight, minHeight: 0 }}>
                                {showLegend ? <div className="evaluation-analytics-bar-layout has-legend">
                                    <div className="evaluation-analytics-bar-canvas">
                                        {renderChart('100%')}
                                    </div>
                                    {renderLegend()}
                                </div> : renderChart('100%')}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
